package fr.frux.core.designsystem.components

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.FastOutLinearInEasing
import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.LinearOutSlowInEasing
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.keyframes
import androidx.compose.animation.core.tween
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.size
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Favorite
import androidx.compose.material.icons.outlined.FavoriteBorder
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.a11y.rememberReduceMotion
import fr.frux.core.designsystem.resources.Res
import fr.frux.core.designsystem.resources.ds_favorite_add
import fr.frux.core.designsystem.resources.ds_favorite_remove
import fr.frux.core.designsystem.theme.fruxColors
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin

/**
 * Cœur des favoris : bascule l'état et récompense le geste par un squash-pop, un anneau et une
 * gerbe de particules.
 *
 * L'animation est déclenchée par le clic, **jamais** dérivée de `isFavorite`. Sur la carte, la
 * liste des favoris se réémet à chaque resynchronisation et la surface n'est jamais démontée
 * (ADR 0009) : un effet piloté par l'état rejouerait la gerbe sur des arbres que personne n'a
 * touchés. Corollaire assumé : le toggle étant optimiste, un refus serveur laisse une gerbe déjà
 * jouée sur un cœur qui redevient vide.
 */
@Composable
fun FruxFavoriteButton(
    isFavorite: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    addLabel: String = stringResource(Res.string.ds_favorite_add),
    removeLabel: String = stringResource(Res.string.ds_favorite_remove),
    burst: Boolean = true,
    activeTint: Color = MaterialTheme.fruxColors.accentCherry,
    inactiveTint: Color = MaterialTheme.colorScheme.onSurfaceVariant,
) {
    val reduceMotion = rememberReduceMotion()
    val scope = rememberCoroutineScope()
    val scale = remember { Animatable(1f) }
    val burstProgress = remember { Animatable(NO_BURST) }
    val sparkTint = MaterialTheme.fruxColors.statusFlowering

    // Le remplissage n'est pas de la motion mais un changement de teinte : il survit à
    // « réduire les animations », comme la transition de couleur de la maquette.
    val fill by animateFloatAsState(
        targetValue = if (isFavorite) 1f else 0f,
        animationSpec = tween(durationMillis = FILL_DURATION_MS),
        label = "favoriteFill",
    )

    Box(
        modifier =
            modifier
                .size(FruxMinTouchTarget)
                // La gerbe se dessine ICI et non dans l'`IconButton` : celui-ci découpe son
                // contenu sur le cercle de 40 dp de sa couche d'état (Material 3), qui rognerait
                // l'anneau (29 dp de rayon) et les huit particules (jusqu'à 34 dp) presque
                // entièrement. Le débordement hors des bornes est voulu — aucun ancêtre ne
                // découpe, hormis la carte quand le cœur en longe le bord.
                .drawBehind { drawBurst(burstProgress.value, activeTint, sparkTint) },
        contentAlignment = Alignment.Center,
    ) {
        IconButton(
            onClick = {
                val adding = !isFavorite
                onClick()
                if (reduceMotion) return@IconButton
                scope.launch {
                    scale.animateTo(1f, if (adding) POP_SPEC else RELEASE_SPEC)
                }
                if (adding && burst) {
                    scope.launch {
                        burstProgress.snapTo(0f)
                        burstProgress.animateTo(
                            targetValue = 1f,
                            animationSpec = tween(BURST_DURATION_MS, easing = LinearEasing),
                        )
                        burstProgress.snapTo(NO_BURST)
                    }
                }
            },
            modifier =
                Modifier
                    .size(FruxMinTouchTarget)
                    .semantics { contentDescription = if (isFavorite) removeLabel else addLabel },
        ) {
            // Seul le squash vit dans une lambda de dessin ici : il redessine sans recomposer.
            Box(
                modifier =
                    Modifier.graphicsLayer {
                        scaleX = scale.value
                        scaleY = scale.value
                    },
            ) {
                // Deux vecteurs distincts là où la maquette interpole l'axe `FILL` d'une fonte
                // variable : le cœur plein monte en opacité par-dessus le contour, qu'il recouvre.
                Icon(
                    imageVector = Icons.Outlined.FavoriteBorder,
                    contentDescription = null,
                    tint = inactiveTint,
                )
                Icon(
                    imageVector = Icons.Filled.Favorite,
                    contentDescription = null,
                    tint = activeTint,
                    modifier = Modifier.graphicsLayer { alpha = fill },
                )
            }
        }
    }
}

/**
 * Gerbe complète : un anneau qui s'ouvre en s'affinant, puis huit particules radiales qui
 * s'échappent en rétrécissant. `progress` négatif = rien à dessiner.
 */
private fun androidx.compose.ui.graphics.drawscope.DrawScope.drawBurst(
    progress: Float,
    tint: Color,
    sparkTint: Color,
) {
    if (progress < 0f || progress >= 1f) return
    val center = Offset(size.width / 2f, size.height / 2f)

    val ring = (progress / RING_SHARE).coerceAtMost(1f)
    if (ring < 1f) {
        val eased = FastOutSlowInEasing.transform(ring)
        drawCircle(
            color = tint,
            radius = (RING_MIN_RADIUS + (RING_MAX_RADIUS - RING_MIN_RADIUS) * eased).toPx(),
            center = center,
            alpha = RING_START_ALPHA * (1f - ring),
            style = Stroke(width = (RING_MAX_WIDTH - (RING_MAX_WIDTH - RING_MIN_WIDTH) * eased).toPx()),
        )
    }

    val eased = LinearOutSlowInEasing.transform(progress)
    for (i in 0 until PARTICLE_COUNT) {
        val angle = (i.toFloat() / PARTICLE_COUNT) * 2f * PI.toFloat() - PI.toFloat() / 2f
        val big = i % 2 == 0
        val distance = (if (big) PARTICLE_FAR_DISTANCE else PARTICLE_NEAR_DISTANCE).toPx()
        val radius = (if (big) PARTICLE_BIG_RADIUS else PARTICLE_SMALL_RADIUS).toPx()
        drawCircle(
            // Une particule sur trois en « floraison » : la maquette casse le camaïeu du cœur.
            color = if (i % 3 == 2) sparkTint else tint,
            radius = radius * (1f - eased * PARTICLE_SHRINK),
            center = center + Offset(cos(angle) * distance * eased, sin(angle) * distance * eased),
            alpha = 1f - progress,
        )
    }
}

/** Hors animation : distinct de 0f, qui est la première image d'une gerbe. */
private const val NO_BURST = -1f

private const val FILL_DURATION_MS = 200
private const val BURST_DURATION_MS = 550
private const val PARTICLE_COUNT = 8
private const val PARTICLE_SHRINK = 0.8f

/** L'anneau (500 ms) s'éteint avant les particules (550 ms). */
private const val RING_SHARE = 500f / BURST_DURATION_MS
private const val RING_START_ALPHA = 0.9f
private val RING_MIN_RADIUS = 5.dp
private val RING_MAX_RADIUS = 29.dp
private val RING_MAX_WIDTH = 8.dp
private val RING_MIN_WIDTH = 1.dp
private val PARTICLE_NEAR_DISTANCE = 26.dp
private val PARTICLE_FAR_DISTANCE = 34.dp
private val PARTICLE_SMALL_RADIUS = 2.5.dp
private val PARTICLE_BIG_RADIUS = 3.5.dp

/**
 * Squash puis dépassement, à l'ajout. L'overshoot vit dans les images clés (1,35) et non dans un
 * easing : `CubicBezierEasing` custom est proscrit ici — points de contrôle hors [0,1] côté CSS,
 * et la cible js a déjà planté dessus.
 */
private val POP_SPEC =
    keyframes {
        durationMillis = 600
        1f at 0 using FastOutLinearInEasing
        0.72f at 90 using FastOutSlowInEasing
        1.35f at 270 using FastOutSlowInEasing
        0.92f at 420 using FastOutSlowInEasing
        1f at 600
    }

/** Retrait : un simple enfoncement, sans récompense. */
private val RELEASE_SPEC =
    keyframes {
        durationMillis = 300
        1f at 0 using FastOutLinearInEasing
        0.78f at 120 using FastOutSlowInEasing
        1f at 300
    }
